// src/utils/propertyCategories.js

// ── SVG icon imports ──────────────────────────────────────────────────────
// Files live in src/assets/svg, so they must be imported as modules (not
// referenced as raw string paths) for the bundler to resolve them correctly.
import saleIcon from '../assets/svg/sale.svg';
import rentIcon from '../assets/svg/rent.svg';
import shortStayIcon from '../assets/svg/short_stay.svg';
import commercialIcon from '../assets/svg/commercial.svg';
import landIcon from '../assets/svg/land.svg';
import developmentsIcon from '../assets/svg/developments.svg';
import tourismIcon from '../assets/svg/tourism.svg';
import servicesIcon from '../assets/svg/services.svg';
import manufacturersIcon from '../assets/svg/manufacturers.svg';
import suppliersIcon from '../assets/svg/suppliers.svg';

export class PropertyCategory {
  constructor({ title, icon, svgPath, subCategories }) {
    this.title = title;
    this.icon = icon;
    this.svgPath = svgPath;
    this.subCategories = subCategories;
  }

  toJSON() {
    return {
      title: this.title,
      subCategories: this.subCategories,
    };
  }

  matchesSearch(searchTerm) {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      this.title.toLowerCase().includes(term) ||
      this.subCategories.some(sub => sub.toLowerCase().includes(term))
    );
  }
}

export const propertyCategories = [
  new PropertyCategory({
    title: 'Residential Sale',
    icon: 'home',
    svgPath: saleIcon,
    subCategories: [
      'Houses / Villas',
      'Apartments / Flats',
      'Townhouses',
      'Condos',
      'Bungalows',
      'Mansions',
    ],
  }),
  new PropertyCategory({
    title: 'Residential Rentals',
    icon: 'vpn_key',
    svgPath: rentIcon,
    subCategories: [
      'Houses for Rent',
      'Apartments for Rent',
      'Rooms / Self-Contained',
      'Shared Housing',
      'Servant Quarters / Boys Quarters',
      'Student Housing / Hostels',
    ],
  }),
  new PropertyCategory({
    title: 'Short Stay & Hospitality',
    icon: 'hotel',
    svgPath: shortStayIcon,
    subCategories: [
      'Hotels',
      'Lodges',
      'Guesthouses',
      'Airbnb / Short-Let',
      'Serviced Apartments',
      'Resorts',
    ],
  }),
  new PropertyCategory({
    title: 'Commercial',
    icon: 'business_center',
    svgPath: commercialIcon,
    subCategories: [
      'Office Spaces',
      'Retail / Shops',
      'Warehouses / Godowns',
      'Factories / Industrial',
      'Fuel Stations',
      'Mixed-Use Buildings',
    ],
  }),
  new PropertyCategory({
    title: 'Land',
    icon: 'landscape',
    svgPath: landIcon,
    subCategories: [
      'Residential Plots',
      'Commercial Plots',
      'Agricultural Land / Farms',
      'Mailo Land',
      'Freehold Land',
      'Industrial Land',
    ],
  }),
  new PropertyCategory({
    title: 'Off-Plan & New Developments',
    icon: 'apartment',
    svgPath: developmentsIcon,
    subCategories: [
      'Off-Plan Apartments',
      'Gated Communities / Estates',
      'Upcoming Developments',
    ],
  }),
  new PropertyCategory({
    title: 'Tourism',
    icon: 'travel_explore',
    svgPath: tourismIcon,
    subCategories: [
      'Tourist Attraction',
      'Tour Operator',
      'Tour Guide',
      'Safari Company',
      'National Park',
      'Wildlife Reserve',
      'Museum',
      'Historical Site',
      'Cultural Site',
      'Beach',
      'Island',
      'Waterfall',
      'Mountain',
      'Lake',
      'Forest',
      'Adventure Tourism',
      'Eco Tourism',
      'Religious Tourism',
      'Cultural Tourism',
      'Community Tourism',
      'Tourism Information Centre',
      'Souvenir Shop',
      'Other',
    ],
  }),
  new PropertyCategory({
    title: 'Other Services',
    icon: 'handshake',
    svgPath: servicesIcon,
    subCategories: [
      'Property Management',
      'Valuations',
      'Architecture',
      'Construction',
      'Interior Designing',
      'Cleaning Services',
      'Mortgage & Financing',
      'Moving & Relocation Services',
    ],
  }),
  new PropertyCategory({
    title: 'Manufacturers',
    icon: 'factory',
    svgPath: manufacturersIcon,
    subCategories: [
      'Cement',
      'Steel',
      'Sand',
      'Aggregate',
      'Stone',
      'Bricks',
      'Blocks',
      'Tiles',
      'Marble & Granite',
      'Roofing Materials',
      'Timber',
      'Plywood',
      'Gypsum',
      'Glass',
      'Aluminium',
      'Doors',
      'Windows',
      'Garage Doors',
      'Security Doors',
      'Plumbing Materials',
      'Pipes & Fittings',
      'Sanitary Ware',
      'Bathroom Fixtures',
      'Water Tanks',
      'Water Pumps',
      'Electrical Materials',
      'Lighting',
      'Solar Equipment',
      'Generators',
      'Cables & Wiring',
      'Switches & Sockets',
      'Electrical Panels',
      'Air Conditioning',
      'Heating Equipment',
      'Ventilation Equipment',
      'Paint',
      'Wall Finishes',
      'Ceiling Materials',
      'Flooring',
      'Wallpaper',
    ],
  }),
  new PropertyCategory({
    title: 'Suppliers',
    icon: 'local_shipping',
    svgPath: suppliersIcon,
    subCategories: [
      'Cement',
      'Steel',
      'Sand',
      'Aggregate',
      'Stone',
      'Bricks',
      'Blocks',
      'Tiles',
      'Marble & Granite',
      'Roofing Materials',
      'Timber',
      'Plywood',
      'Gypsum',
      'Glass',
      'Aluminium',
      'Doors',
      'Windows',
      'Garage Doors',
      'Security Doors',
      'Plumbing Materials',
      'Pipes & Fittings',
      'Sanitary Ware',
      'Bathroom Fixtures',
      'Water Tanks',
      'Water Pumps',
      'Electrical Materials',
      'Lighting',
      'Solar Equipment',
      'Generators',
      'Cables & Wiring',
      'Switches & Sockets',
      'Electrical Panels',
      'Air Conditioning',
      'Heating Equipment',
      'Ventilation Equipment',
      'Paint',
      'Wall Finishes',
      'Ceiling Materials',
      'Flooring',
      'Wallpaper',
    ],
  }),
];

export const getCategoryByTitle = (title) => {
  if (!title) return null;
  return propertyCategories.find(c => c.title === title) || null;
};

export const getCategoryBySubCategory = (subCategory) => {
  if (!subCategory) return null;
  return propertyCategories.find(c => 
    c.subCategories.some(sub => sub.toLowerCase() === subCategory.toLowerCase())
  ) || null;
};

export const getAllSubCategories = () => {
  const all = [];
  propertyCategories.forEach(category => {
    all.push(...category.subCategories);
  });
  return all;
};

export const getSubCategoriesByCategory = (categoryTitle) => {
  const category = getCategoryByTitle(categoryTitle);
  return category ? category.subCategories : [];
};

export const searchPropertyCategories = (searchTerm) => {
  if (!searchTerm) return propertyCategories;
  const term = searchTerm.toLowerCase().trim();
  return propertyCategories.filter(category => category.matchesSearch(term));
};

export const getCategoryIconMap = () => {
  const iconMap = {
    'home': '🏠',
    'vpn_key': '🔑',
    'hotel': '🏨',
    'business_center': '💼',
    'landscape': '🌍',
    'apartment': '🏢',
    'travel_explore': '🧭',
    'handshake': '🤝',
    'factory': '🏭',
    'local_shipping': '🚚',
  };
  return iconMap;
};

export const getCategoryEmoji = (icon) => {
  const map = getCategoryIconMap();
  return map[icon] || '📌';
};

export default {
  propertyCategories,
  PropertyCategory,
  getCategoryByTitle,
  getCategoryBySubCategory,
  getAllSubCategories,
  getSubCategoriesByCategory,
  searchPropertyCategories,
  getCategoryEmoji,
};